/**
 * Pure helpers for auth routing decisions.
 *
 * ``isPublicRoute`` names which pathnames the ``AuthGuard`` should let
 * through without probing the session. ``sanitizeNextPath`` is the
 * open-redirect guard used by the signin screen — it returns a
 * validated same-origin path or ``null`` for anything suspicious.
 *
 * No React imports; framework-agnostic and unit-testable in isolation
 * (see ``__tests__/public-routes.test.ts``).
 */

const PAY_PATH_PREFIX = "/pay/"
const PORTAL_PATH_PREFIX = "/portal/"
const SIGNIN_PATH = "/signin"
// Anonymous, magic-link customer pages: the dunning card-update flow and
// the 3DS/SCA re-auth flow. Both are gated by a signed token in the URL
// (no Django session), so AuthGuard must let them through — otherwise the
// ``/api/auth/me/`` probe 401s and the customer is hard-redirected to the
// staff ``/signin`` screen, stranding the flow (audit #15). NB: the
// ``/installments/failed`` staff queue page is deliberately NOT public.
const CUSTOMER_INSTALLMENT_PATHS = new Set([
  "/installments/update-card",
  "/installments/reauth",
])

export function isPublicRoute(pathname: string): boolean {
  // Next.js may canonicalize URLs with or without a trailing slash
  // depending on the ``trailingSlash`` config, so accept both forms
  // for the bare signin route.
  if (pathname === SIGNIN_PATH || pathname === `${SIGNIN_PATH}/`) {
    return true
  }
  if (pathname === "/pay" || pathname.startsWith(PAY_PATH_PREFIX)) {
    return true
  }
  // Anonymous customer self-service portal (docs/design/customer-portal.md).
  // Authenticated by its own ``billdozer_portal`` cookie session, never the
  // Django staff session — so AuthGuard must let the whole ``/portal`` subtree
  // through, otherwise the ``/api/auth/me/`` probe 401s and the customer is
  // hard-redirected to the staff ``/signin`` (Google) screen.
  if (pathname === "/portal" || pathname.startsWith(PORTAL_PATH_PREFIX)) {
    return true
  }
  // Accept both the bare path and its trailing-slash form; query strings
  // are already stripped from ``pathname`` by ``usePathname``.
  const withoutTrailingSlash = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname
  if (CUSTOMER_INSTALLMENT_PATHS.has(withoutTrailingSlash)) {
    return true
  }
  return false
}

/**
 * Validate a ``?next=`` value against open-redirect tricks. Returns
 * the input when it is a same-origin path, ``null`` otherwise.
 *
 * Rejects:
 *   - ``null`` / empty string pass-through (returns ``null``).
 *   - Values that do not start with ``/`` (bare names like ``evil``).
 *   - Protocol-relative URLs like ``//evil.com`` (would escape origin).
 *   - Absolute URLs with scheme (``http://…``, ``javascript:``).
 *   - Paths containing CR or LF (response-splitting surface).
 *   - ``/signin`` itself — redirecting back to the signin route after
 *     a successful sign-in would loop.
 */
export function sanitizeNextPath(raw: string | null): string | null {
  if (!raw) {
    return null
  }
  if (!raw.startsWith("/")) {
    return null
  }
  if (raw.startsWith("//")) {
    return null
  }
  if (/[\r\n]/.test(raw)) {
    return null
  }
  if (raw === SIGNIN_PATH || raw.startsWith(`${SIGNIN_PATH}?`) || raw.startsWith(`${SIGNIN_PATH}/`)) {
    return null
  }
  return raw
}
